import React from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { formatPrice } from '../../../services/formatPrice';

import clsx from 'clsx';
import Label from './Label';
import {
  CheckIcon,
  ChevronDownIcon,
  MinusIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Badge from '../../Badge';
import useCheckoutState, { checkoutLayouts } from '../../../hooks/useCheckoutState';
import useCheckoutFormHelpers from '../../../hooks/useCheckoutFormHelpers';
import getSubscriptionIntervalWithSlash from '../../../services/translation/getSubscriptionIntervalWithSlash';
import QuantityButton from './QuantityButton';
import QuantityField from './QuantityField';
import { checkHasProductModules } from '../../../enums/modules';
import useCartCost from '../../../hooks/useCartCost';
import checkIsVariantVisible from '../../../lib/checkIsVariantVisible';
import checkIsVariantEnabled from '../../../lib/checkIsVariantEnabled';
import getColors from '../../../lib/getColors';
import { getBulkDiscountsForVariantOption } from '../../../lib/getBulkDiscountsForVariantOption';
import getBulkDiscount from '../../../lib/getBulkDiscount';
import { getImageSrcAndSrcSet } from '../../../services/getUrlFromCdn';
import MultilineText from '../../MultilineText';

function getVariantOptionBulkDiscountAmount(
  variantOption,
  quantity = 1,
  subtotal,
  bulkDiscounts = [],
) {
  const bulkDiscountsForVariantOption = getBulkDiscountsForVariantOption(
    bulkDiscounts,
    variantOption._id,
  );

  const { bulkDiscountAmount } = getBulkDiscount(bulkDiscountsForVariantOption, quantity, subtotal);

  return bulkDiscountAmount;
}

function isSoldOut(variant, option) {
  if (!variant.manageVariantOptionStock) return false;

  if (typeof option.stock !== 'number') return false;

  return Number(option.stock) <= 0;
}

const Variants = () => {
  const { isPreview, checkoutLayout, isScreenshot, product, checkout, form, seller, query } =
    useCheckoutState();
  const { productPriceAmount, setupFeeAmount } = useCartCost();

  const hasProductModules = checkHasProductModules({
    checkout,
    seller,
    isScreenshot,
    query,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: product.discountedFromPrice,
    trialPeriodDays: product.trialPeriodDays,
    startDate: product.startDate,
  });

  const { variants: productVariants } = product;

  // filter out disabled variants
  const variants = productVariants.filter(variant => variant.status === 'enabled');

  if (!variants?.length) return '';

  if (variants.length === 1 && !variants[0].layout?.collapseVariantOptions) {
    const variant = variants[0];

    const enabled = checkIsVariantEnabled(variant, form.variants, variants);
    const visible = checkIsVariantVisible(variant, query);

    if ((!enabled || !visible) && !isPreview) return '';

    return (
      <div className="cp_variants">
        <div
          className={clsx(
            `cp_variant-wrapper cp_variant-${variant.reference}-wrapper`,
            'border-b border-gray-100 pb-4 px-4',
            {
              'opacity-25': (!enabled || !visible) && isPreview,
            },
          )}
        >
          <Variant
            variant={variant}
            currency={product.currency}
            locale={checkout.locale}
            soldOutText={checkout.soldOutText}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx('cp_variants', {
        'mx-4':
          checkoutLayout === checkoutLayouts.SPLIT || checkoutLayout === checkoutLayouts.DOUBLE,
      })}
    >
      {variants.map(variant => {
        const enabled = checkIsVariantEnabled(variant, form.variants, variants);

        const visible = checkIsVariantVisible(variant, query);

        if ((!enabled || !visible) && !isPreview) return '';

        // not accordion
        if (!variant.layout?.collapseVariantOptions) {
          return (
            <div
              key={variant._id}
              className={clsx(
                `cp_variant-wrapper cp_variant-${variant.reference}-wrapper`,
                'last:border-b border-gray-100 last:pb-4 pt-4',
                {
                  'opacity-25': (!enabled || !visible) && isPreview,
                  'first:pt-0': checkoutLayout === checkoutLayouts.DOUBLE,
                  'px-4': checkoutLayout === checkoutLayouts.CENTER,
                  'first:border-t':
                    hasProductModules &&
                    (checkoutLayout === checkoutLayouts.CENTER ||
                      checkoutLayout === checkoutLayouts.SPLIT),
                },
              )}
            >
              <Variant
                variant={variant}
                currency={product.currency}
                locale={checkout.locale}
                soldOutText={checkout.soldOutText}
              />
            </div>
          );
        }

        const variantOptions = variant.options.filter(option => option.status === 'enabled');

        const selectedOptions = variantOptions
          .filter(option => {
            const formVariant = form.variants[variant._id];

            if (!formVariant) return false;

            const selectedOption = formVariant.find(v => v.optionId === option._id);

            if (!selectedOption) return false;

            return selectedOption.quantity > 0;
          })
          .map(option => {
            const formVariant = form.variants[variant._id];

            if (!formVariant) return false;

            const selectedOption = formVariant.find(v => v.optionId === option._id);

            if (!selectedOption) return false;

            return {
              ...option,
              quantity: selectedOption.quantity,
            };
          });

        // accordion
        return (
          <Disclosure
            key={variant._id}
            as="div"
            className={clsx(
              'cp_variant-accordion-wrapper',
              'first:border-t border-b border-gray-100',
              {
                'opacity-25': (!enabled || !visible) && isPreview,
                'px-4': checkoutLayout === checkoutLayouts.CENTER,
              },
            )}
          >
            {({ open, close }) => (
              <>
                <DisclosureButton className={clsx('cp_variant-accordion', 'py-2 my-2 w-full')}>
                  <div className="cp_variant-accordion-label-wrapper flex justify-between items-center">
                    <Label
                      className="cp_variant-accordion-label"
                      style={{ marginBottom: 0, cursor: 'inherit' }}
                      required={variant.required}
                    >
                      {variant.name}
                    </Label>
                    <ChevronDownIcon className="cp_variant-accordion-label-icon h-5 w-5" />
                  </div>

                  {/* selected variant option in closed accordion */}
                  {selectedOptions &&
                    selectedOptions.length > 0 &&
                    !open &&
                    selectedOptions.map(selectedOption => (
                      <div
                        className="cp_variant-accordion-selected-option text-left mt-2 text-gray-900 flex"
                        key={selectedOption.optionId}
                      >
                        {variant.selectionType === 'quantity' && (
                          <>
                            <div className="cp_variant-accordion-selected-option-quantity-wrapper mr-1 whitespace-nowrap">
                              {selectedOption.quantity}
                              <XMarkIcon className="cp_variant-accordion-selected-option-quantity-icon w-3 h-3 mt-0.5 inline-block" />
                            </div>

                            <SelectedAccordionVariantOptionLabel
                              option={selectedOption}
                              showName={variant.layout?.showVariantOptionNames}
                              showPrice={variant.layout?.showVariantOptionPrices}
                              showPriceSign={variant.layout?.showVariantOptionPriceSign}
                              textAlign={variant.layout?.textAlign}
                              imageSize={variant.layout?.imageSize}
                              soldOutText={checkout.soldOutText}
                              currency={product.currency}
                              locale={checkout.locale}
                              isSoldOut={isSoldOut(variant, selectedOption)}
                            />
                          </>
                        )}

                        {variant.selectionType !== 'quantity' && (
                          <>
                            <CheckIcon className="cp_variant-accordion-selected-option-icon w-5 h-5 mr-1 mt-0.5 text-gray-400" />

                            <SelectedAccordionVariantOptionLabel
                              option={selectedOption}
                              showName={variant.layout?.showVariantOptionNames}
                              showPrice={variant.layout?.showVariantOptionPrices}
                              showPriceSign={variant.layout?.showVariantOptionPriceSign}
                              textAlign={variant.layout?.textAlign}
                              imageSize={variant.layout?.imageSize}
                              soldOutText={checkout.soldOutText}
                              currency={product.currency}
                              locale={checkout.locale}
                              isSoldOut={isSoldOut(variant, selectedOption)}
                            />
                          </>
                        )}
                      </div>
                    ))}
                </DisclosureButton>

                <DisclosurePanel
                  className={clsx(
                    `cp_variant-wrapper cp_variant-${variant.reference}-wrapper pb-4`,
                  )}
                >
                  <Variant
                    variant={variant}
                    onSelectVariantOption={close}
                    currency={product.currency}
                    locale={checkout.locale}
                    soldOutText={checkout.soldOutText}
                  />
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
        );
      })}
    </div>
  );
};

const Variant = ({ variant, onSelectVariantOption, currency, locale, soldOutText }) => {
  const { form } = useCheckoutState();
  const { variants: formVariants } = form;

  const { setVariantOption, unsetVariantOption } = useCheckoutFormHelpers();

  const variantOptions = variant.options.filter(option => option.status === 'enabled');

  // if there is quantity selection, don't use radio buttons, use divs but keep the same styles
  if (variant.selectionType === 'quantity') {
    return (
      <div className={clsx(`cp_variant cp_variant-${variant.reference}`)}>
        {variant.layout?.showVariantName && !variant.layout?.collapseVariantOptions && (
          <Label required={variant.required} className="cp_variant-label">
            {variant.name}
          </Label>
        )}

        <div
          className={clsx(`cp_variant-items`, {
            'grid gap-2': variant.layout?.variantOptionLayout === 'grid',
            '-space-y-px': variant.layout?.variantOptionLayout !== 'grid',
          })}
          style={{
            ...(variant.layout?.variantOptionLayout === 'grid' && {
              gridTemplateColumns: `repeat(${variant.layout?.variantOptionColumns}, minmax(0, 1fr))`,
            }),
          }}
        >
          {variantOptions.map((option, index) => {
            const value = formVariants[variant._id]?.find(v => v.optionId === option._id);

            if (!value) return null;

            function setQuantity(quantity) {
              setVariantOption(variant._id, option._id, quantity);
            }

            const selectedOption = {
              ...option,
              quantity: value.quantity,
            };

            return (
              <div className={clsx(`cp_variant-item block w-full`)} key={option._id}>
                <div
                  className={clsx(
                    'cp_variant-option relative flex focus:outline-none shadow-input h-full transition-shadow',
                    {
                      'p-2': variant.layout?.spacing === 'compact',
                      'p-4': variant.layout?.spacing === 'comfortable' || !variant.layout?.spacing,
                      'px-2':
                        variant.layout?.variantOptionLayout === 'grid' &&
                        variant.layout?.variantOptionColumns > 3,
                      'rounded-md': variant.layout?.variantOptionLayout === 'grid',
                      'rounded-tl-md rounded-tr-md': index === 0,
                      'rounded-bl-md rounded-br-md': index === variantOptions.length - 1,
                    },
                  )}
                >
                  <div className="cp_variant-option-label-wrapper w-full space-y-2">
                    <VariantOptionLabel
                      variant={variant}
                      setQuantity={setQuantity}
                      option={selectedOption}
                      soldOutText={soldOutText}
                      currency={currency}
                      locale={locale}
                      isSoldOut={isSoldOut(variant, option)}
                      showName={variant.layout?.showVariantOptionNames}
                      showPrice={variant.layout?.showVariantOptionPrices}
                      showPriceSign={variant.layout?.showVariantOptionPriceSign}
                      textAlign={variant.layout?.textAlign}
                      imageSize={variant.layout?.imageSize}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(`cp_variant cp_variant-${variant.reference}`)}>
      {variant.layout?.showVariantName && !variant.layout?.collapseVariantOptions && (
        <Label required={variant.required} className="cp_variant-label">
          {variant.name}
        </Label>
      )}

      <div
        className={clsx(`cp_variant-items`, {
          'grid gap-2': variant.layout?.variantOptionLayout === 'grid',
          '-space-y-px': variant.layout?.variantOptionLayout !== 'grid',
        })}
        style={{
          ...(variant.layout?.variantOptionLayout === 'grid' && {
            gridTemplateColumns: `repeat(${variant.layout?.variantOptionColumns}, minmax(0, 1fr))`,
          }),
        }}
      >
        {variantOptions.map((option, index) => {
          const formVariant = formVariants[variant._id];
          const formOption = formVariant?.find(v => v.optionId === option._id);
          const isSelected = formOption?.quantity > 0;

          function onOptionClick(e, optionId) {
            // Prevent the button's click handler if an <a> was clicked inside
            if (e.target.closest('a')) {
              return;
            }
            // if clicked on the currently selected option
            if (isSelected) {
              if (variant.selectionType === 'multiple') {
                unsetVariantOption(variant._id, optionId);
              } else {
                // if variant is required
                // set the same variant option so that accordion still closes
                if (variant.required) {
                  setVariantOption(variant._id, optionId, 1);
                } else {
                  // if variant is not required, unset the selected variant option
                  unsetVariantOption(variant._id, optionId);
                }
              }
            } else {
              if (variant.selectionType === 'multiple') {
                // if multiple select is allowed, set the quantity to 1
                // leave the quantity of other variant options in the same variant as is
                setVariantOption(variant._id, optionId, 1);
              } else {
                // if multiple select is not allowed, set the quantity to 1 and set the quantity of all other variant options in the same variant to 0
                formVariants?.[variant._id]?.forEach(formVariantOption => {
                  if (formVariantOption.optionId !== optionId) {
                    unsetVariantOption(variant._id, formVariantOption.optionId);
                  }
                });

                setVariantOption(variant._id, optionId, 1);

                if (onSelectVariantOption && typeof onSelectVariantOption === 'function') {
                  onSelectVariantOption();
                }
              }
            }
          }

          const selectedOption = {
            ...option,
            quantity: formOption?.quantity,
          };

          return (
            <button
              onClick={e => onOptionClick(e, option._id)}
              type="button"
              className={clsx(`cp_variant-item block w-full`, {
                'cursor-not-allowed': isSoldOut(variant, option),
              })}
              key={option._id}
              disabled={isSoldOut(variant, option)}
            >
              <div
                className={clsx(
                  'cp_variant-option relative flex focus:outline-none h-full border border-gray-200 bg-[var(--variant-background-color)]',
                  {
                    'p-2': variant.layout?.spacing === 'compact',
                    'p-4': variant.layout?.spacing === 'comfortable' || !variant.layout?.spacing,
                    'px-2':
                      variant.layout?.variantOptionLayout === 'grid' &&
                      variant.layout?.variantOptionColumns > 3,
                    'rounded-md': variant.layout?.variantOptionLayout === 'grid',
                    'rounded-tl-md rounded-tr-md': index === 0,
                    'rounded-bl-md rounded-br-md': index === variantOptions.length - 1,
                    [`cp_variant-option--selected z-10 ring-1 ring-[var(--variant-selected-border-color)] !border-[var(--variant-selected-border-color)] !bg-[var(--variant-selected-background-color)]`]:
                      isSelected,
                  },
                )}
              >
                <div className="flex justify-between w-full overflow-hidden">
                  <VariantOptionLabel
                    variant={variant}
                    option={selectedOption}
                    soldOutText={soldOutText}
                    currency={currency}
                    locale={locale}
                    isSoldOut={isSoldOut(variant, option)}
                    showName={variant.layout?.showVariantOptionNames}
                    showPrice={variant.layout?.showVariantOptionPrices}
                    showPriceSign={variant.layout?.showVariantOptionPriceSign}
                    textAlign={variant.layout?.textAlign}
                    imageSize={variant.layout?.imageSize}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

function VariantOptionQuantitySelect({ textAlign, option, selectedQuantity, setQuantity }) {
  return (
    <div className="w-full">
      <div
        className={clsx('cp_variant-quantity-wrapper flex items-stretch', {
          'justify-center': textAlign === 'center',
        })}
      >
        <QuantityButton
          className="cp_variant-quantity-button-minus"
          onClick={event => {
            event.stopPropagation();

            if (selectedQuantity <= 0) {
              return;
            }

            setQuantity(Number(selectedQuantity - 1));
          }}
        >
          <MinusIcon className="cp_variant-quantity-button-icon cp_variant-quantity-button-minus-icon w-5 h-5" />
        </QuantityButton>
        <QuantityField
          className="cp_variant-quantity-input"
          id={option.optionId}
          name={option.optionId}
          value={selectedQuantity}
          onChange={setQuantity}
          required={false}
          min={0}
          max={9999}
        />
        <QuantityButton
          className="cp_variant-quantity-button-plus"
          type="button"
          onClick={event => {
            event.stopPropagation();

            setQuantity(Number(selectedQuantity + 1));
          }}
        >
          <PlusIcon className="cp_variant-quantity-button-icon cp_variant-quantity-button-plus-icon w-5 h-5" />
        </QuantityButton>
      </div>
    </div>
  );
}

function SelectedAccordionVariantOptionLabel({
  option,
  currency,
  locale,
  soldOutText,
  isSoldOut,
  showName,
  showPrice,
  showPriceSign,
  textAlign = 'left',
}) {
  const { product, checkout, checkoutLayout, isPreview } = useCheckoutState();
  const { planIterations } = useCartCost();
  const { secondaryTextColor } = getColors(checkout, checkoutLayout);

  const quantity = option.quantity || 1;
  const subtotal = option.additionalChargeAmount * quantity;
  const variantOptionBulkDiscountAmount = getVariantOptionBulkDiscountAmount(
    option,
    quantity,
    subtotal,
    product.discounts,
  );

  const variantOptionTotal = subtotal - variantOptionBulkDiscountAmount;
  const image = getImageSrcAndSrcSetHelper({ url: option.image?.url, isPreview });

  return (
    <div className="cp_variant-option-label-wrapper flex flex-wrap w-full gap-2">
      <div
        className={clsx('cp_variant-option-label-container flex flex-nowrap w-full gap-x-4', {
          'text-left': textAlign === 'left',
        })}
      >
        {image && (
          <div
            className={clsx(
              'cp_variant-image-wrapper w-12 h-12 rounded overflow-hidden flex-shrink-0',
            )}
          >
            <img
              src={image.src}
              srcSet={image.srcSet}
              className="cp_variant-image object-cover h-full w-full"
            />
          </div>
        )}

        <div className="cp_variant-text-wrapper gap-x-1.5 gap-y-0.5 flex flex-col">
          {showName && (
            <div className="cp_variant-name overflow-hidden flex-shrink-0 font-medium">
              <MultilineText dangerouslySetInnerHTML={{ __html: option.name }} />
            </div>
          )}
        </div>

        <div className="ml-auto">
          {((showPrice && variantOptionTotal) || isSoldOut) && (
            <div
              className={clsx('cp_variant-price-wrapper gap-x-1.5 gap-y-0.5 flex flex-col', {
                'text-right': showName && textAlign === 'left',
                'flex-1': !showName,
              })}
            >
              {showPrice && variantOptionTotal && (
                <>
                  <div
                    className={clsx('cp_variant-price space-y-1 w-full', {
                      'text-right': textAlign === 'left',
                    })}
                  >
                    {showPriceSign && variantOptionTotal > 0 && (
                      <span className="cp_variant-price-plus-sign">+</span>
                    )}

                    <span className="cp_variant-price-amount">
                      {formatPrice({
                        amount: variantOptionTotal,
                        currency: currency,
                        locale: locale,
                        style: 'currency',
                      })}
                    </span>

                    {product.type === 'subscription' && option.type === 'recurring' && (
                      <span className="cp_variant-price-interval">
                        {' '}
                        {getSubscriptionIntervalWithSlash(
                          product.interval,
                          product.intervalCount,
                          checkout,
                        )}
                      </span>
                    )}
                  </div>

                  {product.type === 'subscription' &&
                    option.type === 'recurring' &&
                    planIterations > 1 && (
                      <div
                        className="cp_variant-price text-sm"
                        style={{ color: secondaryTextColor }}
                      >
                        <span className="cp_variant-price-ends-after-text">
                          {checkout.endsAfterText}
                        </span>{' '}
                        <span className="cp_variant-price-plan-iterations">{planIterations}</span>{' '}
                        <span className="cp_variant-price-ends-after-payments-text">
                          {checkout.endsAfterPaymentsText}
                        </span>
                      </div>
                    )}
                </>
              )}

              {isSoldOut && (
                <Badge
                  className={clsx(
                    'cp_sold-out cp_variant-soldout bg-gray-900 text-white whitespace-nowrap',
                    {
                      'mr-auto': textAlign === 'left',
                    },
                  )}
                >
                  {soldOutText}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VariantOptionLabel({
  variant,
  setQuantity = () => {},
  option,
  currency,
  locale,
  soldOutText,
  isSoldOut,
  showName,
  showPrice,
  showPriceSign,
  textAlign = 'left',
  imageSize = 'small',
}) {
  const { product, checkout, checkoutLayout, isPreview } = useCheckoutState();
  const { planIterations } = useCartCost();
  const { secondaryTextColor } = getColors(checkout, checkoutLayout);

  const variantOptionPrice = option.additionalChargeAmount;
  const quantity = option.quantity;
  const subtotal = option.additionalChargeAmount * quantity;
  const variantOptionBulkDiscountAmount = getVariantOptionBulkDiscountAmount(
    option,
    quantity,
    subtotal,
    product.discounts,
  );
  const variantOptionTotal = subtotal - variantOptionBulkDiscountAmount;

  const currentVariantOptionPrice = variantOptionTotal
    ? variantOptionTotal / quantity
    : variantOptionPrice;

  // if the option has a discountedFromPrice, use it
  // otherwise, if the current variant option price is less than the variant option price, use the variant option price as the discounted from price
  // this would happen when there are bulk discounts applied to the variant option
  const discountedFromPrice = option.discountedFromPrice
    ? option.discountedFromPrice
    : currentVariantOptionPrice < variantOptionPrice
      ? variantOptionPrice
      : '';

  const showVariantOptionPrice = showPrice && (variantOptionPrice === 0 || variantOptionPrice > 0);
  const image = getImageSrcAndSrcSetHelper({ url: option.image?.url, imageSize, isPreview });

  return (
    <div className="cp_variant-option-label flex flex-wrap w-full gap-2">
      {textAlign === 'left' && (
        <div
          className={clsx('cp_variant-option-left flex flex-nowrap w-full gap-x-4', {
            'text-left': textAlign === 'left',
            'flex-col gap-y-2': variant.layout?.variantOptionLayout === 'grid',
          })}
        >
          {image && (
            <div
              className={clsx('cp_variant-image-wrapper rounded overflow-hidden flex-shrink-0', {
                'w-16 h-16': imageSize === 'small',
                'w-32 h-32': imageSize === 'medium',
                'w-64 h-64': imageSize === 'large',
              })}
            >
              <img
                src={image.src}
                srcSet={image.srcSet}
                className="cp_variant-image object-cover h-full w-full"
              />
            </div>
          )}

          <div className="cp_variant-text-wrapper gap-x-1.5 gap-y-0.5 flex flex-col">
            {showName && (
              <div className="cp_variant-name overflow-hidden flex-shrink-0 font-medium">
                <MultilineText dangerouslySetInnerHTML={{ __html: option.name }} />
              </div>
            )}
            {((showPrice && variantOptionPrice) || isSoldOut) && (
              <div
                className={clsx('cp_variant-price-wrapper gap-x-1.5 gap-y-0.5 flex flex-col', {
                  'flex-1': !showName,
                })}
              >
                {showPrice && variantOptionPrice > 0 && showVariantOptionPrice && (
                  <>
                    <div className={clsx('cp_variant-price space-y-1 w-full')}>
                      {discountedFromPrice && (
                        <span className="cp_variant-discounted-from text-gray-600 line-through mr-1.5">
                          {formatPrice({
                            amount: discountedFromPrice,
                            currency: currency,
                            locale: locale,
                            style: 'currency',
                          })}
                        </span>
                      )}

                      {showPriceSign && variantOptionPrice > 0 && (
                        <span className="cp_variant-price-plus-sign">+</span>
                      )}

                      <span className="cp_variant-price-amount">
                        {formatPrice({
                          amount: currentVariantOptionPrice || variantOptionPrice,
                          currency: currency,
                          locale: locale,
                          style: 'currency',
                        })}
                      </span>

                      {product.type === 'subscription' && option.type === 'recurring' && (
                        <span className="cp_variant-price-interval">
                          {' '}
                          {getSubscriptionIntervalWithSlash(
                            product.interval,
                            product.intervalCount,
                            checkout,
                          )}
                        </span>
                      )}
                    </div>

                    {product.type === 'subscription' &&
                      option.type === 'recurring' &&
                      planIterations > 1 && (
                        <div
                          className="cp_variant-price text-sm"
                          style={{ color: secondaryTextColor }}
                        >
                          <span className="cp_variant-price-ends-after-text">
                            {checkout.endsAfterText}
                          </span>{' '}
                          <span className="cp_variant-price-plan-iterations">{planIterations}</span>{' '}
                          <span className="cp_variant-price-ends-after-payments-text">
                            {checkout.endsAfterPaymentsText}
                          </span>
                        </div>
                      )}
                  </>
                )}
                {isSoldOut && (
                  <div
                    className={clsx('cp_variant-price-wrapper gap-x-1.5 gap-y-0.5 flex flex-col', {
                      'flex-1': !showName,
                    })}
                  >
                    <Badge
                      className={clsx(
                        'cp_sold-out cp_variant-soldout bg-gray-900 text-white whitespace-nowrap',
                        {
                          'mr-auto': textAlign === 'left',
                        },
                      )}
                    >
                      {soldOutText}
                    </Badge>
                  </div>
                )}
              </div>
            )}

            {option.description && (
              <div className="cp_variant-description text-gray-700 text-sm">
                <MultilineText dangerouslySetInnerHTML={{ __html: option.description }} />
              </div>
            )}
          </div>

          {variant.selectionType === 'quantity' && !isSoldOut && (
            <div
              className={clsx('cp_variant-option-quantity-wrapper flex flex-col gap-y-2', {
                'ml-auto': variant.layout?.variantOptionLayout !== 'grid',
              })}
            >
              <VariantOptionQuantitySelect
                textAlign={variant.layout?.textAlign}
                option={option}
                selectedQuantity={quantity}
                setQuantity={setQuantity}
              />

              {variantOptionTotal > 0 && (
                <div className="cp_variant-total text-center text-gray-700 text-sm">
                  {checkout.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: variantOptionTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {textAlign === 'center' && (
        <div
          className={clsx('cp_variant-option-center flex flex-grow w-full gap-x-4 gap-y-0.5', {
            'text-center flex-col': textAlign === 'center',
          })}
        >
          {image && (
            <div
              className={clsx(
                'cp_variant-image-wrapper rounded overflow-hidden flex-shrink-0 flex-grow-0 mb-2',
                {
                  'mx-auto': textAlign === 'center',
                  'w-16 h-16': imageSize === 'small',
                  'w-32 h-32': imageSize === 'medium',
                  'w-64 h-64': imageSize === 'large',
                },
              )}
            >
              <img
                src={image.src}
                srcSet={image.srcSet}
                className="cp_variant-image object-cover h-full w-full"
              />
            </div>
          )}

          {showName && (
            <div className="cp_variant-name overflow-hidden flex-shrink-0 font-medium">
              <MultilineText dangerouslySetInnerHTML={{ __html: option.name }} />
            </div>
          )}

          {((showPrice && variantOptionPrice) || isSoldOut) && (
            <div
              className={clsx('cp_variant-price-wrapper gap-x-1.5 gap-y-0.5 flex', {
                'flex-1': !showName,
                'flex-col text-center justify-center': textAlign === 'center',
              })}
            >
              {showPrice && variantOptionPrice > 0 && showVariantOptionPrice && (
                <>
                  <div className={clsx('cp_variant-price space-y-1 w-full')}>
                    {discountedFromPrice && (
                      <span className="cp_variant-discounted-from text-gray-600 line-through mr-1.5">
                        {formatPrice({
                          amount: discountedFromPrice,
                          currency: currency,
                          locale: locale,
                          style: 'currency',
                        })}
                      </span>
                    )}

                    {showPriceSign && variantOptionPrice > 0 && (
                      <span className="cp_variant-price-plus-sign">+</span>
                    )}

                    <span className="cp_variant-price-amount">
                      {formatPrice({
                        amount: currentVariantOptionPrice || variantOptionPrice,
                        currency: currency,
                        locale: locale,
                        style: 'currency',
                      })}
                    </span>

                    {product.type === 'subscription' && option.type === 'recurring' && (
                      <span className="cp_variant-price-interval">
                        {' '}
                        {getSubscriptionIntervalWithSlash(
                          product.interval,
                          product.intervalCount,
                          checkout,
                        )}
                      </span>
                    )}
                  </div>

                  {product.type === 'subscription' &&
                    option.type === 'recurring' &&
                    planIterations > 1 && (
                      <div
                        className="cp_variant-price text-sm"
                        style={{ color: secondaryTextColor }}
                      >
                        <span className="cp_variant-price-ends-after-text">
                          {checkout.endsAfterText}
                        </span>{' '}
                        <span className="cp_variant-price-plan-iterations">{planIterations}</span>{' '}
                        <span className="cp_variant-price-ends-after-payments-text">
                          {checkout.endsAfterPaymentsText}
                        </span>
                      </div>
                    )}
                </>
              )}

              {isSoldOut && (
                <Badge
                  className={clsx(
                    'cp_sold-out cp_variant-soldout bg-gray-900 text-white whitespace-nowrap',
                    {
                      'mr-auto': textAlign === 'left',
                    },
                  )}
                >
                  {soldOutText}
                </Badge>
              )}
            </div>
          )}
          {option.description && (
            <div className="cp_variant-description text-gray-700 text-sm">
              <MultilineText dangerouslySetInnerHTML={{ __html: option.description }} />
            </div>
          )}

          {variant.selectionType === 'quantity' && !isSoldOut && (
            <div className={clsx('cp_variant-option-quantity-wrapper flex flex-col gap-y-2 mt-2')}>
              <VariantOptionQuantitySelect
                textAlign={variant.layout?.textAlign}
                option={option}
                selectedQuantity={quantity}
                setQuantity={setQuantity}
              />

              {variantOptionTotal > 0 && (
                <div className="cp_variant-total text-center text-gray-700 text-sm">
                  {checkout.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: variantOptionTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {textAlign === 'stack' && (
        <div
          className={clsx('cp_variant-option-stack w-full', {
            'text-left': textAlign === 'stack',
          })}
        >
          {image && (
            <div
              className={clsx(
                'cp_variant-image-wrapper rounded overflow-hidden flex-shrink-0 flex-grow-0 mb-2',
                {
                  'w-16 h-16': imageSize === 'small',
                  'w-32 h-32': imageSize === 'medium',
                  'w-64 h-64': imageSize === 'large',
                },
              )}
            >
              <img
                src={image.src}
                srcSet={image.srcSet}
                className="cp_variant-image object-cover h-full w-full"
              />
            </div>
          )}

          {showName && (
            <div className="cp_variant-name overflow-hidden flex-shrink-0 font-medium">
              <MultilineText dangerouslySetInnerHTML={{ __html: option.name }} />
            </div>
          )}

          {((showPrice && variantOptionPrice) || isSoldOut) && (
            <div
              className={clsx(
                'cp_variant-price-wrapper gap-x-1.5 gap-y-0.5 inline-flex flex-col items-start',
                {
                  'flex-1': !showName,
                },
              )}
            >
              {showPrice && variantOptionPrice > 0 && showVariantOptionPrice && (
                <>
                  <div className={clsx('cp_variant-price space-y-1 w-full')}>
                    {discountedFromPrice && (
                      <span className="cp_variant-discounted-from text-gray-600 line-through mr-1.5">
                        {formatPrice({
                          amount: discountedFromPrice,
                          currency: currency,
                          locale: locale,
                          style: 'currency',
                        })}
                      </span>
                    )}

                    {showPriceSign && option.additionalChargeAmount > 0 && (
                      <span className="cp_variant-price-plus-sign">+</span>
                    )}

                    <span className="cp_variant-price-amount">
                      {formatPrice({
                        amount: variantOptionPrice,
                        currency: currency,
                        locale: locale,
                        style: 'currency',
                      })}
                    </span>

                    {product.type === 'subscription' && option.type === 'recurring' && (
                      <span className="cp_variant-price-interval">
                        {' '}
                        {getSubscriptionIntervalWithSlash(
                          product.interval,
                          product.intervalCount,
                          checkout,
                        )}
                      </span>
                    )}
                  </div>

                  {product.type === 'subscription' &&
                    option.type === 'recurring' &&
                    planIterations > 1 && (
                      <div
                        className="cp_variant-price text-sm"
                        style={{ color: secondaryTextColor }}
                      >
                        <span className="cp_variant-price-ends-after-text">
                          {checkout.endsAfterText}
                        </span>{' '}
                        <span className="cp_variant-price-plan-iterations">{planIterations}</span>{' '}
                        <span className="cp_variant-price-ends-after-payments-text">
                          {checkout.endsAfterPaymentsText}
                        </span>
                      </div>
                    )}
                </>
              )}

              {isSoldOut && (
                <Badge
                  className={clsx(
                    'cp_sold-out cp_variant-soldout bg-gray-900 text-white whitespace-nowrap',
                    {
                      'mr-auto': textAlign === 'left',
                    },
                  )}
                >
                  {soldOutText}
                </Badge>
              )}
            </div>
          )}

          {option.description && (
            <div className="cp_variant-description text-gray-700 text-sm">
              <MultilineText dangerouslySetInnerHTML={{ __html: option.description }} />
            </div>
          )}

          {variant.selectionType === 'quantity' && !isSoldOut && (
            <div className={clsx('cp_variant-option-quantity-wrapper flex flex-col gap-y-2 mt-2')}>
              <VariantOptionQuantitySelect
                textAlign={variant.layout?.textAlign}
                option={option}
                selectedQuantity={quantity}
                setQuantity={setQuantity}
              />

              {variantOptionTotal > 0 && (
                <div className="cp_variant-total text-gray-700 text-sm">
                  {checkout.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: variantOptionTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getImageSrcAndSrcSetHelper({ url, imageSize = 'small', isPreview = false }) {
  if (url == null) return;

  const { height, width } = (() => {
    switch (imageSize) {
      case 'small':
        return { height: 64, width: 64 };
      case 'medium':
        return { height: 128, width: 128 };
      case 'large':
      default:
        return { height: 256, width: 256 };
    }
  })();

  return getImageSrcAndSrcSet({ url, width, height, isPreview });
}

export default Variants;
